import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRevAllNodesWithStatus } from "../../../redux/procedureRevisionSlice";
import { getAllVersionHistories } from "../../../redux/versionHistorySlice";
import { generateAlphanumericSuffix } from "../../../util/AppUtils";

/**
 * useVideoData Hook
 *
 * Manages video data fetching and state management using getRevAllNodes
 * Calculates proper page titles and sibling orders based on node hierarchy
 *
 * @param {Object} params
 * @param {string} params.mode - "add" or "edit"
 * @param {string} params.id - Video ID reference (for edit mode)
 * @param {string} params.partnumber - Part number
 * @param {string} params.revision - Revision
 * @param {string} params.partId - Part ID
 * @param {string} params.revisionId - Revision ID
 * @param {string} params.section_id_ref - Section ID reference
 * @param {string} params.step_id_ref - Step ID reference
 * @param {string} params.substep_id_ref - Substep ID reference (query param)
 * @param {string} params.node_id_ref - Node ID reference (query param)
 *
 * @returns {Object} { loading, videoData, versionId, procedureId, lastSavedText, pageTitle, breadcrumbText, siblingOrder }
 */
const useVideoData = ({
  mode,
  id,
  partnumber,
  revision,
  partId,
  revisionId,
  section_id_ref,
  step_id_ref,
  substep_id_ref,
  node_id_ref,
}) => {
  const dispatch = useDispatch();
  const { currentStatus, item_code_type } = useSelector((state) => state.procedureRevision);

  const [loading, setLoading] = useState(true);
  const [videoData, setVideoData] = useState(null);
  const [versionId, setVersionId] = useState("");
  const [procedureId, setProcedureId] = useState("");
  const [lastSavedText, setLastSavedText] = useState("");
  const [pageTitle, setPageTitle] = useState("Video");
  const [breadcrumbText, setBreadcrumbText] = useState("");
  const [siblingOrder, setSiblingOrder] = useState(null);
  const [allNodesData, setAllNodesData] = useState([]);

  const [sectionId, setSectionId] = useState(null);
  const [stepId, setStepId] = useState(null);
  const [substepId, setSubstepId] = useState("");

  // Get version histories from Redux
  const { versionHistories } = useSelector((state) => state.versionHistory);

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        setLoading(true);

        // Fetch all nodes using getRevAllNodesWithStatus
        const result = await dispatch(
          getRevAllNodesWithStatus({
            partId: partId,
            revisionId: revisionId,
          })
        );

        if (result.payload) {
          const nodes = result.payload.data;
          setAllNodesData(nodes);

          // Find current section and step
          const currentSection = nodes.find(
            (section) => section.tree_node_id === section_id_ref
          );

          const currentStep = currentSection?.steps?.find(
            (step) => step.tree_node_id === step_id_ref
          );

          setSectionId(currentSection.section_id_ref);
          setStepId(currentStep.step_id_ref);

          // Calculate video count and page title
          let totalVideoCount = 0;
          let calculatedSiblingOrder = null;

          // Count all videos across all sections and steps
          // Sort sections by sibling order to ensure correct counting
          const sortedSections = [...nodes].sort((a, b) => {
            const orderA =
              a.sibling_order || a.procedure_tree?.sibling_order || 0;
            const orderB =
              b.sibling_order || b.procedure_tree?.sibling_order || 0;
            return parseInt(orderA) - parseInt(orderB);
          });

          for (const section of sortedSections) {
            if (section.steps && Array.isArray(section.steps)) {
              // Sort steps by sibling order
              const sortedSteps = [...section.steps].sort((a, b) => {
                const orderA =
                  a.sibling_order || a.procedure_tree?.sibling_order || 0;
                const orderB =
                  b.sibling_order || b.procedure_tree?.sibling_order || 0;
                return parseInt(orderA) - parseInt(orderB);
              });

              for (const step of sortedSteps) {
                // Count videos in step
                if (step.children && Array.isArray(step.children)) {
                  const stepVideos = step.children.filter(
                    (child) => child.type === "video"
                  );
                  totalVideoCount += stepVideos.length;
                }

                // Count videos in substeps
                if (step.substeps && Array.isArray(step.substeps)) {
                  // Sort substeps by sibling order
                  const sortedSubsteps = [...step.substeps].sort((a, b) => {
                    const orderA =
                      a.sibling_order || a.procedure_tree?.sibling_order || 0;
                    const orderB =
                      b.sibling_order || b.procedure_tree?.sibling_order || 0;
                    return parseInt(orderA) - parseInt(orderB);
                  });

                  for (const substep of sortedSubsteps) {
                    if (substep.children && Array.isArray(substep.children)) {
                      const substepVideos = substep.children.filter(
                        (child) => child.type === "video"
                      );
                      totalVideoCount += substepVideos.length;
                    }
                  }
                }
              }
            }
          }

          // Set page title based on mode
          let title;
          if (mode === "edit") {
            // For edit mode, find the current video's position in the sequence
            const currentVideoNumber = findVideoPosition(nodes, id);
            title = `Video ${currentVideoNumber}`;
          } else {
            // For add mode, use total count + 1
            const videoNumber = totalVideoCount + 1;
            title = `Video ${videoNumber}`;
          }
          setPageTitle(title);

          // Calculate sibling order based on context
          if (substep_id_ref) {
            // Case 1: Adding video to substep
            const targetSubstep = currentStep?.substeps?.find(
              (substep) => substep.tree_node_id === substep_id_ref
            );
            setSubstepId(targetSubstep?.substep_id_ref);
            if (node_id_ref) {
              // When adding after a specific child node in substep
              const targetNode = findNodeById(nodes, node_id_ref);
              if (targetNode) {
                const targetSiblingOrder =
                  targetNode.sibling_order ||
                  targetNode.procedure_tree?.sibling_order ||
                  0;
                calculatedSiblingOrder = parseInt(targetSiblingOrder, 10) + 1;
              } else {
                calculatedSiblingOrder = 1;
              }
            } else if (
              targetSubstep?.children &&
              Array.isArray(targetSubstep.children)
            ) {
              calculatedSiblingOrder = targetSubstep.children.length + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          } else if (node_id_ref) {
            // Case 2: Adding video after existing child node in step
            const targetNode = findNodeById(nodes, node_id_ref);
            if (targetNode) {
              const targetSiblingOrder =
                targetNode.sibling_order ||
                targetNode.procedure_tree?.sibling_order ||
                0;
              calculatedSiblingOrder = parseInt(targetSiblingOrder, 10) + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          } else {
            // Case 3: Adding video to step
            if (currentStep?.children && Array.isArray(currentStep.children)) {
              calculatedSiblingOrder = currentStep.children.length + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          }

          setSiblingOrder(calculatedSiblingOrder);

          // Build breadcrumb text
          const sectionIndex = nodes.findIndex(
            (section) => section.tree_node_id === section_id_ref
          );
          const sectionNumber = sectionIndex + 1;

          // Calculate step number by counting all steps across all sections before current section
          // plus the index within the current section
          let totalStepsBeforeCurrentSection = 0;
          for (let i = 0; i < sectionIndex; i++) {
            if (
              nodes[i].steps &&
              Array.isArray(nodes[i].steps)
            ) {
              totalStepsBeforeCurrentSection += nodes[i].steps.length;
            }
          }

          const stepIndexInCurrentSection = currentSection?.steps?.findIndex(
            (step) => step.tree_node_id === step_id_ref
          );
          const stepNumber =
            totalStepsBeforeCurrentSection + stepIndexInCurrentSection + 1;

          let breadcrumb = `${partnumber} > Rev ${revision} > Section ${sectionNumber}`;

          if (substep_id_ref) {
            // Sort substeps by sibling_order to get correct index
            const sortedSubsteps = currentStep?.substeps
              ? [...currentStep.substeps].sort((a, b) => {
                  const orderA =
                    a.sibling_order || a.procedure_tree?.sibling_order || 0;
                  const orderB =
                    b.sibling_order || b.procedure_tree?.sibling_order || 0;
                  const intA = parseInt(orderA, 10);
                  const intB = parseInt(orderB, 10);
                  if (intA !== intB) return intA - intB;
                  const dateA = new Date(a.created_at || 0);
                  const dateB = new Date(b.created_at || 0);
                  return dateB - dateA;
                })
              : [];

            const substepIndex = sortedSubsteps.findIndex(
              (substep) => substep.tree_node_id === substep_id_ref
            );
            const substepLetter = generateAlphanumericSuffix(
              substepIndex >= 0 ? substepIndex : 0
            );
            breadcrumb += ` > Step ${stepNumber} > Substep ${stepNumber}${substepLetter}`;
          } else {
            breadcrumb += ` > Step ${stepNumber}`;
          }

          breadcrumb += ` > ${title}`;
          setBreadcrumbText(breadcrumb);
        }

        // For edit mode, find video data from allNodesData
        if (mode === "edit" && id) {
          const videoNode = findNodeById(result.payload?.data, id);

          if (videoNode) {

            // Set basic fields from the node
            setVersionId(videoNode.version_id_ref || "");
            setProcedureId(videoNode.procedure_tree_ref || "");

            // Fetch version histories if version ID exists
            if (videoNode.version_id_ref) {
              dispatch(
                getAllVersionHistories({ versionId: videoNode.version_id_ref })
              );
            }

            // Set video data from node (no API calls)
            setVideoData(videoNode);
          } else {
          }
        }
      } catch (error) {
        console.error("Error fetching video data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (partId && revisionId && section_id_ref && step_id_ref) {
      fetchVideoData();
    }
  }, [
    mode,
    id,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    node_id_ref,
    dispatch,
  ]);

  // Update last saved text when version history is loaded
  useEffect(() => {
    if (mode !== "edit" || !videoData || versionHistories.length === 0) return;

    // Get the latest version history entry (most recent audit)
    const latestVersion = versionHistories[0];
    if (latestVersion) {
      const userName =
        `${latestVersion.first_name || ""} ${
          latestVersion.last_name || ""
        }`.trim() || "Unknown User";

      const timestamp =
        latestVersion.audit_date ||
        videoData.atn_updated_at ||
        videoData.atn_created_at;
      if (timestamp) {
        const { generateLastSavedText } = require("../../../util/stepUtils");
        setLastSavedText(generateLastSavedText(timestamp, userName));
      }
    }
  }, [mode, videoData, versionHistories]);

  // Helper function to find node by ID recursively
  // Leaf nodes: tree_node_id, detail.video_tree_node_id / canvas ids, etc.
  const findNodeById = (nodes, nodeId) => {
    for (const section of nodes) {
      if (section.tree_node_id === nodeId) return section;

      if (section.steps && Array.isArray(section.steps)) {
        for (const step of section.steps) {
          if (step.tree_node_id === nodeId) return step;

          if (step.children && Array.isArray(step.children)) {
            for (const child of step.children) {
              if (
                child.tree_node_id === nodeId ||
                child.detail?.canvas_tree_node_id === nodeId ||
                child.canvas_tree_node_id === nodeId ||
                child.detail?.video_tree_node_id === nodeId ||
                child.video_tree_node_id === nodeId
              ) {
                return child;
              }
            }
          }

          if (step.substeps && Array.isArray(step.substeps)) {
            for (const substep of step.substeps) {
              if (substep.tree_node_id === nodeId) return substep;

              if (substep.children && Array.isArray(substep.children)) {
                for (const child of substep.children) {
                  if (
                    child.tree_node_id === nodeId ||
                    child.detail?.canvas_tree_node_id === nodeId ||
                    child.canvas_tree_node_id === nodeId ||
                    child.detail?.video_tree_node_id === nodeId ||
                    child.video_tree_node_id === nodeId
                  ) {
                    return child;
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  const matchesVideoRouteId = (child, videoId) =>
    child.tree_node_id === videoId ||
    child.detail?.video_tree_node_id === videoId ||
    child.video_tree_node_id === videoId ||
    child.detail?.element_id === videoId ||
    child.element_id === videoId;

  // Helper function to find video position in the sequence
  const findVideoPosition = (nodes, videoId) => {
    let counter = 0;

    // Sort sections by sibling order
    const sortedSections = [...nodes].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order || 0;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order || 0;
      return parseInt(orderA) - parseInt(orderB);
    });

    for (const section of sortedSections) {
      if (section.steps && Array.isArray(section.steps)) {
        // Sort steps by sibling order
        const sortedSteps = [...section.steps].sort((a, b) => {
          const orderA =
            a.sibling_order || a.procedure_tree?.sibling_order || 0;
          const orderB =
            b.sibling_order || b.procedure_tree?.sibling_order || 0;
          return parseInt(orderA) - parseInt(orderB);
        });

        for (const step of sortedSteps) {
          // Count videos in step
          if (step.children && Array.isArray(step.children)) {
            const stepVideos = step.children.filter(
              (child) => child.type === "video"
            );

            // Check if our video is in this step
            const currentVideo = stepVideos.find((child) =>
              matchesVideoRouteId(child, videoId),
            );
            if (currentVideo) {
              const positionInStep = stepVideos.findIndex((child) =>
                matchesVideoRouteId(child, videoId),
              );
              return counter + positionInStep + 1;
            }

            counter += stepVideos.length;
          }

          // Count videos in substeps
          if (step.substeps && Array.isArray(step.substeps)) {
            // Sort substeps by sibling order
            const sortedSubsteps = [...step.substeps].sort((a, b) => {
              const orderA =
                a.sibling_order || a.procedure_tree?.sibling_order || 0;
              const orderB =
                b.sibling_order || b.procedure_tree?.sibling_order || 0;
              return parseInt(orderA) - parseInt(orderB);
            });

            for (const substep of sortedSubsteps) {
              if (substep.children && Array.isArray(substep.children)) {
                const substepVideos = substep.children.filter(
                  (child) => child.type === "video"
                );

                // Check if our video is in this substep
                const currentVideo = substepVideos.find((child) =>
                  matchesVideoRouteId(child, videoId),
                );
                if (currentVideo) {
                  const positionInSubstep = substepVideos.findIndex((child) =>
                    matchesVideoRouteId(child, videoId),
                  );
                  return counter + positionInSubstep + 1;
                }

                counter += substepVideos.length;
              }
            }
          }
        }
      }
    }

    // If not found, return 1 as fallback
    return 1;
  };

  return {
    loading,
    videoData,
    versionId,
    procedureId,
    lastSavedText,
    pageTitle,
    breadcrumbText,
    siblingOrder,
    allNodesData,
    sectionId,
    stepId,
    substepId,
    currentStatus,
    item_code_type,
  };
};

export default useVideoData;
